import $ from 'jquery';
import PaymentFieldType from './PaymentFieldType';
import SpreedlyException from './SpreedlyException';
import SpreedlyValidationException from './SpreedlyValidationException';
import ValidationResult from './ValidationResult';

interface ValidationMessageConfiguration {
    type: PaymentFieldType;
    required: string;
    invalid: string;
}

interface TokenizeErrorResult {
    attribute: string;
    key: string;
    message: string;
    status: number;
}

export interface SpreedlyConfiguration {
    environmentKey: string;
    acceptHeader: string;
}

export default class SpreedlyService {

    private readonly validationMessageConfiguration: ValidationMessageConfiguration[] = [
        {
            type: PaymentFieldType.number,
            required: 'The Card Number is required.',
            invalid: 'The Card Number is invalid.',
        },
        {
            type: PaymentFieldType.cvv,
            required: 'Security Code is required',
            invalid: 'Security Code is invalid',
        },
    ];

    private readonly validationPromiseQueue: Array<{ Resolve: any, Reject: any }> = [];
    private readonly requestPaymentPromiseQueue: Array<{ Resolve: any, Reject: any }> = [];

    public constructor(
        private readonly configuration: SpreedlyConfiguration,
        private readonly spreedlyCardNumber: JQuery,
        private readonly spreedlyCvv: JQuery,
    ) {
    }

    public initialize(numberStyle: any, cvvStyle: any): void {
        const [spreedlyCardNumberId, spreedlyCvvId] = this.getSpreedlyFieldIds(
            this.spreedlyCardNumber,
            this.spreedlyCvv,
        );

        window.Spreedly.init(this.configuration.environmentKey, {
            numberEl: spreedlyCardNumberId,
            cvvEl: spreedlyCvvId,
        });

        window.Spreedly.on('ready', () => {
            window.Spreedly.setStyle(
                PaymentFieldType[PaymentFieldType.number],
                this.createInlineStyle(numberStyle),
            );
            window.Spreedly.setStyle(
                PaymentFieldType[PaymentFieldType.cvv],
                this.createInlineStyle(cvvStyle),
            );
        });

        this.handleValidationResult();
        this.handleTokenizeSuccess();
        this.handleTokenizeError();
    }

    public async validate(): Promise<ValidationResult[]> {
        return new Promise<ValidationResult[]>((resolve, reject) => {
            window.Spreedly.validate();
            // We push a resolve to an array (resolveQueue) because we can't resolve a promise inside this method.
            // A promise will be resolved by window.Spreedly.on('validation') callback
            // which called after we get a validation result from Spreedly
            this.validationPromiseQueue.push({ Resolve: resolve, Reject: reject });
        });
    }

    public async requestToken(request: ICreditCardTokenRequest, selectedPaymentType: string): Promise<string> {
        try {
            const paymentData = await new Promise((resolve, reject) => {
                window.Spreedly.tokenizeCreditCard(request);
                this.requestPaymentPromiseQueue.push({ Resolve: resolve, Reject: reject });
            });

            const validationResult = this.validateMatchedCreditCardType(paymentData, selectedPaymentType);
            if (validationResult.length) {
                throw new SpreedlyValidationException(validationResult);
            }
            return JSON.stringify(paymentData);
        } catch (error : any) {
            if (error instanceof SpreedlyValidationException) {
                throw error;
            }

            const handledErrorKey = [
                'errors.invalid',
                'errors.expired',
                'errors.blank',
                'errors.blank_card_type',
            ];

            const anyHandledErrors = error.tokenizeError.some(error =>
                handledErrorKey.includes(error.key),
            );

            if (!anyHandledErrors) {
                // Other unhandled errors, let server side handle them.
                throw new SpreedlyException(JSON.stringify(error.tokenizeError));
            }

            const validationResult = error.tokenizeError.map(error =>
                this.createValidationErrorMessageFromTokenizeResult(error),
            ).filter(result => result !== null);
            throw new SpreedlyValidationException(validationResult);
        }
    }

    public handleIFrameFieldsEvent(
        onInput: (
            currentFieldType: PaymentFieldType,
            validationResult: ValidationResult[],
            inputProperties: ICreditCardInputProperties,
        ) => void,
        onBlur: (currentFieldType: PaymentFieldType) => void,
    ): void {
        window.Spreedly.on(
            'fieldEvent',
            (
                name: string,
                type: string,
                activeEl: string,
                inputProperties: ICreditCardInputProperties,
            ) => {
                const currentFieldType = PaymentFieldType[name];
                if (type === 'input') {
                    // We get card number and CVV validation from Spreedly together
                    onInput(currentFieldType, this.createValidationResult(inputProperties), inputProperties);
                }

                if (type === 'blur') {
                    onBlur(currentFieldType);
                }
            },
        );
    }

    public setBrowserInfo(): void {
        //        width x height
        // '01' - 250px x 400px
        // '02' - 390px x 400px
        // '03' - 500px x 600px
        // '04' - 600px x 400px
        // '05' - fullscreen
        //
        // must also match the css set for ".challenge-iframe-class"
        // 02 is recommended by our payment gateway as it's compatible with both 3DS1 and 3DS2
        const browserInfo = window.Spreedly.ThreeDS.serialize(
            '02',
            this.configuration.acceptHeader,
        );
        const browserInfoInput = $('input[name="threeDSBrowserInfo"]');
        browserInfoInput.val(browserInfo);
    }

    public focus(paymentFieldType: PaymentFieldType): void {
        window.Spreedly.transferFocus(PaymentFieldType[paymentFieldType]);
    }

    private createInlineStyle(styleObj: any): string {
        return Object.keys(styleObj)
            .map(key => `${key}: ${styleObj[key]}`)
            .join(';');
    }

    private validateMatchedCreditCardType(
        paymentData: any,
        selectedPaymentType: string,
    ): ValidationResult[] {
        if (
            selectedPaymentType &&
            selectedPaymentType !== paymentData.card_type
        ) {
            return [
                {
                    PaymentFieldType: PaymentFieldType.number,
                    ErrorMessage:
                        'The credit card number does not match the credit card type.',
                },
            ];
        }
        return [];
    }

    private createValidationResult(
        inputProperties: ICreditCardInputProperties,
    ): ValidationResult[] {
        const lowerCaseInputProperties = Object.keys(inputProperties).reduce(
            (obj, key) => {
                obj[key.toLowerCase()] = inputProperties[key];
                return obj;
            },
            {},
        );

        const validationResult: ValidationResult[] = this.validationMessageConfiguration
            .filter(
                field =>
                    lowerCaseInputProperties[
                    `valid${PaymentFieldType[field.type].toLowerCase()}`
                    ] === false,
            )
            .map(field => {
                const message =
                    lowerCaseInputProperties[
                        `${PaymentFieldType[field.type].toLowerCase()}length`
                    ] === 0
                        ? field.required
                        : field.invalid;
                return { PaymentFieldType: field.type, ErrorMessage: message };
            });

        return validationResult;
    }

    private handleValidationResult() {
        window.Spreedly.on(
            'validation',
            (inputProperties: ICreditCardInputProperties) => {
                const promiseQueue = this.validationPromiseQueue.pop() as any;
                const resolve = promiseQueue.Resolve;
                resolve(this.createValidationResult(inputProperties));
            },
        );
    }

    private handleTokenizeSuccess(): void {
        window.Spreedly.on('paymentMethod', (_, paymentData) => {
            const promiseQueue = this.requestPaymentPromiseQueue.pop() as any;
            const resolve = promiseQueue.Resolve;
            resolve(paymentData);
        });
    }

    private handleTokenizeError(): void {
        window.Spreedly.on('errors', (errors: TokenizeErrorResult[]) => {
            const promiseQueue = this.requestPaymentPromiseQueue.pop() as any;
            const reject = promiseQueue.Reject;
            reject({ tokenizeError: errors });
        });
    }

    private createValidationErrorMessageFromTokenizeResult(error: TokenizeErrorResult): ValidationResult {
        switch (error.attribute) {
            case 'full_name':
            case 'first_name':
            case 'last_name':
                return { PaymentFieldType: PaymentFieldType.cardholdername, ErrorMessage: error.message };
            case 'number':
                return { PaymentFieldType: PaymentFieldType.number, ErrorMessage: error.message };
            case 'month':
            case 'year':
                return { PaymentFieldType: PaymentFieldType.cardexpiry, ErrorMessage: error.message };
            case 'verification_value':
                return { PaymentFieldType: PaymentFieldType.cvv, ErrorMessage: error.message };
            default:
                return (null as any) as ValidationResult;
        }
    }

    private getSpreedlyFieldIds(
        spreedlyCardNumber: JQuery,
        spreedlyCvv: JQuery,
    ): string[] {
        const spreedlyFields = {
            spreedlyCardNumber,
            spreedlyCvv,
        };

        const errorMessages = Object.keys(spreedlyFields)
            .filter(key => !spreedlyFields[key].attr('id'))
            .map(
                key =>
                    `\nelement for '${key}' does not have id attribute value`,
            );

        if (errorMessages.length > 0) {
            throw new Error(errorMessages.join(''));
        }

        return Object.keys(spreedlyFields).map(key =>
            spreedlyFields[key].attr('id'),
        );
    }
}
